import { type AreaProperties, type SlowZoneVehicleLimit } from 'src/api/fm/zones/zones.model'

function NoParking({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>No parking zone</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function NoRiding({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>No riding zone</p>
            <p>
                Disable throttle: <strong>{rules.noRiding_isEnforced ? 'On' : 'Off'}</strong>
            </p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function NoStart({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>No start zone</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function OperationsArea({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Operations area</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function BountyArea({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Bounty area</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function SoftMpz({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Soft MPZ Area</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function FreeFloating({ name, rules }: Pick<AreaProperties, 'name' | 'rules'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Free Floating Area</p>
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function SubZone({ name, id }: Pick<AreaProperties, 'name' | 'id'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Sub-zone</p>
            {id && (
                <p>
                    ID: <strong>{id}</strong>
                </p>
            )}
        </>
    )
}

function formatVehicleLimit({ max_speed_kph, max_assist_level }: SlowZoneVehicleLimit) {
    return [max_speed_kph !== undefined && `${max_speed_kph} km/h`, max_assist_level && `assist ≤ ${max_assist_level}`]
        .filter(Boolean)
        .join(', ')
}

function SlowZone({ name, rules, properties }: Pick<AreaProperties, 'name' | 'rules' | 'properties'>) {
    const limits = Object.entries(properties?.vehicle_limits ?? {}).sort(([a], [b]) => a.localeCompare(b))
    return (
        <>
            <strong>{name}</strong>
            <p>Slow zone</p>
            {limits.length > 0
                ? limits.map(([vehicleType, limit]) => (
                      <p key={vehicleType}>
                          {vehicleType}: <strong>{formatVehicleLimit(limit)}</strong>
                      </p>
                  ))
                : rules.slowZone_maxSpeed && (
                      <p>
                          Max speed: <strong>{rules.slowZone_maxSpeed}</strong>
                      </p>
                  )}
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function IncentivizedParking({ name, rules, id }: Pick<AreaProperties, 'name' | 'rules' | 'id'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Incentivized parking zone</p>
            {id && (
                <p>
                    ID: <strong>{id}</strong>
                </p>
            )}
            <p>
                Incentive reward: <strong>{rules.incentive_rewardAmount} VOI</strong>
            </p>
            {rules.parking_area_capacity && (
                <p>
                    Max parking capacity: <strong>{rules.parking_area_capacity}</strong>
                </p>
            )}
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

function ParkingSpot({ name, rules, id }: Pick<AreaProperties, 'name' | 'rules' | 'id'>) {
    return (
        <>
            <strong>{name}</strong>
            <p>Parking spot</p>
            {id && (
                <p>
                    ID: <strong>{id}</strong>
                </p>
            )}
            {rules.parking_area_capacity && (
                <p>
                    Max parking capacity: <strong>{rules.parking_area_capacity}</strong>
                </p>
            )}
            {rules.vehicle_types && (
                <p>
                    Vehicle Types: <strong>{rules.vehicle_types.join(',')}</strong>
                </p>
            )}
        </>
    )
}

export function AreaTooltipContent({ areaProperties }: { areaProperties: AreaProperties }) {
    const { area_type, name, rules, id, properties } = areaProperties

    switch (area_type) {
        case 'no-parking':
            return <NoParking name={name} rules={rules} />
        case 'no-riding':
            return <NoRiding name={name} rules={rules} />
        case 'no-start':
            return <NoStart name={name} rules={rules} />
        case 'operations':
            return <OperationsArea name={name} rules={rules} />
        case 'sub-zone':
            return <SubZone name={name} id={id} />
        case 'bounty':
            return <BountyArea name={name} rules={rules} />
        case 'slow-zone':
            return <SlowZone name={name} rules={rules} properties={properties} />
        case 'soft-mpz':
            return <SoftMpz name={name} rules={rules} />
        case 'free-floating':
            return <FreeFloating name={name} rules={rules} />
        case 'incentive':
            return <IncentivizedParking name={name} rules={rules} id={id} />
        case 'parking-spot':
            return <ParkingSpot name={name} rules={rules} id={id} />
        default:
            return null
    }
}
